import {
  TEAM_MEMBER_ROLE_LABELS,
  teamLineupAccountSchema,
  teamLineupSchema,
  teamMemberRoleSchema,
  teamMemberRoles,
  type AdminTeam,
  type TeamGame,
  type TeamLineup,
  type TeamLineupAccount,
  type TeamMember,
  type TeamMemberRole,
} from '@growup/site-shared';
import {
  Autocomplete,
  Button,
  Card,
  Checkbox,
  DateInput,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
} from '@growup/ui';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';

const BASE = '/api/site/teams';

/** Hoje em Lisboa, `aaaa-mm-dd`. */
const today = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Lisbon' }).format(new Date());
const day = (d: string) => d.split('-').reverse().join('/');

const searchAccounts = (q: string) =>
  getJson(`${BASE}/lineup-accounts?q=${encodeURIComponent(q)}`, z.array(teamLineupAccountSchema));

/**
 * Lineup e epocas de uma equipa (parte 2, docs/equipas). Quem sai fica no
 * historico; apagar e so para enganos das ultimas 24 horas.
 */
export function LineupCard({ team, game }: { team: AdminTeam; game: TeamGame | undefined }) {
  const [lineup, setLineup] = useState<TeamLineup | null>(null);
  const [seasonId, setSeasonId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const bySeason = team.lineupMode === 'season';

  const show = useCallback((next: TeamLineup) => {
    setLineup(next);
    setSeasonId((current) =>
      current !== null && next.seasons.some((s) => s.id === current)
        ? current
        : (next.seasons[0]?.id ?? null),
    );
  }, []);

  useEffect(() => {
    getJson(`${BASE}/${team.id}/lineup`, teamLineupSchema)
      .then(show)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [team.id, show]);

  /** Corre um pedido que devolve o lineup; `true` se correu bem. */
  const run = async (method: string, path: string, body?: unknown): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const next = teamLineupSchema.parse(await send(method, `${BASE}${path}`, body));
      show(next);
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (lineup === null) {
    return (
      <Card headingLevel={2} title="Lineup">
        {error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>}
      </Card>
    );
  }

  const season = lineup.seasons.find((s) => s.id === seasonId) ?? null;
  const rows = lineup.members.filter((m) => !bySeason || m.seasonId === seasonId);
  const current = rows.filter((m) => m.leftOn === null);
  const history = rows.filter((m) => m.leftOn !== null);
  const roles = teamMemberRoles(team.kind);

  return (
    <Card headingLevel={2} title="Lineup">
      <div className="flex flex-col gap-4">
        {bySeason ? (
          <Seasons
            lineup={lineup}
            seasonId={seasonId}
            busy={busy}
            onPick={setSeasonId}
            onCreate={(body) => run('POST', `/${team.id}/seasons`, body)}
            onRemove={(id) => run('DELETE', `/seasons/${id}`)}
          />
        ) : (
          <p className="text-gu-ink-muted">Lineup contínuo: só datas de entrada e saída.</p>
        )}
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {bySeason && season === null ? (
          <Notice tone="info">Crie a primeira época para montar o lineup.</Notice>
        ) : (
          <>
            {current.length === 0 ? <p>Ainda ninguém neste lineup.</p> : null}
            <ul className="flex flex-col">
              {current.map((m) => (
                <li key={m.id}>
                  <MemberRow
                    member={m}
                    game={game}
                    roles={roles}
                    busy={busy}
                    onSave={(body) => run('PUT', `/lineup/${m.id}`, body)}
                    onLeave={(leftOn) => run('POST', `/lineup/${m.id}/leave`, { leftOn })}
                    onRemove={() => run('DELETE', `/lineup/${m.id}`)}
                  />
                </li>
              ))}
            </ul>
            <AddMember
              key={seasonId ?? 'continuo'}
              game={game}
              roles={roles}
              busy={busy}
              defaultDay={season && season.startsOn > today() ? season.startsOn : today()}
              onAdd={(body) => run('POST', `/${team.id}/lineup`, { ...body, seasonId })}
            />
            {history.length > 0 ? (
              <div className="flex flex-col gap-1">
                <h3 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
                  Histórico do lineup
                </h3>
                <ul className="text-gu-ink-muted">
                  {history.map((m) => (
                    <li key={m.id}>
                      {m.login} · {TEAM_MEMBER_ROLE_LABELS[m.role]} · {day(m.joinedOn)} a{' '}
                      {day(m.leftOn ?? '')}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Card>
  );
}

function Seasons({
  lineup,
  seasonId,
  busy,
  onPick,
  onCreate,
  onRemove,
}: {
  lineup: TeamLineup;
  seasonId: string | null;
  busy: boolean;
  onPick: (id: string) => void;
  onCreate: (body: unknown) => Promise<boolean>;
  onRemove: (id: string) => Promise<boolean>;
}) {
  const [adding, setAdding] = useState(lineup.seasons.length === 0);
  const [name, setName] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [copy, setCopy] = useState(true);
  const season = lineup.seasons.find((s) => s.id === seasonId);
  const empty = season ? !lineup.members.some((m) => m.seasonId === season.id) : false;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void onCreate({
      name,
      startsOn,
      endsOn,
      copyFrom: copy && seasonId !== null ? seasonId : null,
    }).then((ok) => {
      if (!ok) return;
      setAdding(false);
      setName('');
      setStartsOn('');
      setEndsOn('');
    });
  };

  return (
    <div className="flex flex-col gap-3">
      {lineup.seasons.length > 0 ? (
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Época" className="min-w-48">
            <Select value={seasonId ?? ''} onChange={(e) => onPick(e.target.value)}>
              {lineup.seasons.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({day(s.startsOn)} a {day(s.endsOn)})
                </option>
              ))}
            </Select>
          </Field>
          {!adding ? (
            <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
              Nova época
            </Button>
          ) : null}
          {season && empty ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => void onRemove(season.id)}
            >
              Apagar época
            </Button>
          ) : null}
        </div>
      ) : null}
      {adding ? (
        <form className="flex flex-col gap-3 border-2 border-gu-line p-3" onSubmit={submit}>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Nome da época" hint="Por exemplo 2026/27.">
              <Input value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Início">
              <DateInput value={startsOn} onChange={setStartsOn} />
            </Field>
            <Field label="Fim">
              <DateInput value={endsOn} onChange={setEndsOn} min={startsOn || undefined} />
            </Field>
          </div>
          {season ? (
            <Checkbox
              label={`Começar com o lineup de ${season.name} (quem não saiu)`}
              checked={copy}
              onChange={(e) => setCopy(e.target.checked)}
            />
          ) : null}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={busy}>
              Criar época
            </Button>
            {lineup.seasons.length > 0 ? (
              <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(false)}>
                Cancelar
              </Button>
            ) : null}
          </div>
        </form>
      ) : null}
    </div>
  );
}

interface MemberFields {
  role: TeamMemberRole;
  gameRoleId: string | null;
  jerseyNumber: number | null;
  joinedOn: string;
}

/** Funcao, funcao de jogo, numero e entrada: o mesmo no adicionar e no editar. */
function MemberFieldsForm({
  value,
  game,
  roles,
  onChange,
}: {
  value: MemberFields;
  game: TeamGame | undefined;
  roles: readonly TeamMemberRole[];
  onChange: (v: MemberFields) => void;
}) {
  const gameRoles = game?.roles ?? [];
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Field label="Função">
        <Select
          value={value.role}
          onChange={(e) => onChange({ ...value, role: teamMemberRoleSchema.parse(e.target.value) })}
        >
          {roles.map((r) => (
            <option key={r} value={r}>
              {TEAM_MEMBER_ROLE_LABELS[r]}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Função de jogo" optional>
        <Select
          value={value.gameRoleId ?? ''}
          disabled={gameRoles.length === 0}
          onChange={(e) => onChange({ ...value, gameRoleId: e.target.value || null })}
        >
          <option value="">{gameRoles.length === 0 ? 'Este jogo não tem' : '—'}</option>
          {gameRoles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Número" optional>
        <Input
          inputMode="numeric"
          maxLength={2}
          value={value.jerseyNumber?.toString() ?? ''}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            onChange({ ...value, jerseyNumber: digits === '' ? null : Number(digits) });
          }}
        />
      </Field>
      <Field label="Entrada">
        <DateInput
          value={value.joinedOn}
          onChange={(joinedOn) => onChange({ ...value, joinedOn })}
        />
      </Field>
    </div>
  );
}

function AddMember({
  game,
  roles,
  busy,
  defaultDay,
  onAdd,
}: {
  game: TeamGame | undefined;
  roles: readonly TeamMemberRole[];
  busy: boolean;
  defaultDay: string;
  onAdd: (body: MemberFields & { accountId: string }) => Promise<boolean>;
}) {
  const [q, setQ] = useState('');
  const [account, setAccount] = useState<TeamLineupAccount | null>(null);
  const [fields, setFields] = useState<MemberFields>({
    role: 'player',
    gameRoleId: null,
    jerseyNumber: null,
    joinedOn: defaultDay,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!account) return;
    void onAdd({ ...fields, accountId: account.id }).then((ok) => {
      if (!ok) return;
      setAccount(null);
      setFields((f) => ({ ...f, gameRoleId: null, jerseyNumber: null }));
    });
  };

  return (
    <form className="flex flex-col gap-3 border-t border-gu-line pt-4" onSubmit={submit}>
      <h3 className="font-sub text-label font-bold uppercase">Adicionar ao lineup</h3>
      {account ? (
        <div className="flex items-center gap-3">
          <span className="font-semibold">{account.login}</span>
          {account.youth ? <StatusBadge tone="neutral">Jovem da Arena</StatusBadge> : null}
          <Button type="button" size="sm" variant="ghost" onClick={() => setAccount(null)}>
            Trocar
          </Button>
        </div>
      ) : (
        <Field label="Membro" hint="Só membros e associados.">
          <Autocomplete
            value={q}
            onChange={setQ}
            search={searchAccounts}
            getKey={(a) => a.id}
            renderItem={(a) => (
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-semibold">{a.login}</span>
                {a.youth ? (
                  <span className="text-caption text-gu-ink-muted">jovem da Arena</span>
                ) : null}
              </span>
            )}
            onSelect={(a) => {
              setQ('');
              setAccount(a);
            }}
            placeholder="Procurar pelo login…"
          />
        </Field>
      )}
      <MemberFieldsForm value={fields} game={game} roles={roles} onChange={setFields} />
      <div>
        <Button type="submit" size="sm" disabled={busy || account === null}>
          Adicionar
        </Button>
      </div>
    </form>
  );
}

function MemberRow({
  member,
  game,
  roles,
  busy,
  onSave,
  onLeave,
  onRemove,
}: {
  member: TeamMember;
  game: TeamGame | undefined;
  roles: readonly TeamMemberRole[];
  busy: boolean;
  onSave: (body: MemberFields) => Promise<boolean>;
  onLeave: (leftOn: string) => Promise<boolean>;
  onRemove: () => Promise<boolean>;
}) {
  const [mode, setMode] = useState<'view' | 'edit' | 'leave'>('view');
  const [fields, setFields] = useState<MemberFields>(member);
  const [leftOn, setLeftOn] = useState(today());
  const gameRole = game?.roles.find((r) => r.id === member.gameRoleId)?.name;

  return (
    <div className="flex flex-col gap-3 border-b border-gu-line py-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="w-8 text-right font-display text-title">{member.jerseyNumber ?? ''}</span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-semibold">
            {member.login}
            {member.youth ? (
              <span className="ml-2 text-caption font-normal text-gu-ink-muted">
                {member.photoAuthorized ? 'jovem' : 'jovem · sem foto'}
              </span>
            ) : null}
          </span>
          {member.competeAuthorized === false ? (
            <StatusBadge tone="warning" className="self-start">
              Pendente: falta a autorização do encarregado
            </StatusBadge>
          ) : null}
          <span className="text-caption text-gu-ink-muted">
            {[TEAM_MEMBER_ROLE_LABELS[member.role], gameRole, `desde ${day(member.joinedOn)}`]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        {mode === 'view' ? (
          <div className="flex w-full justify-end gap-1 sm:w-auto">
            <Button size="sm" variant="secondary" onClick={() => setMode('edit')}>
              Editar
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setMode('leave')}>
              Saiu
            </Button>
            {member.deletable ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                title="Só para enganos das últimas 24 horas"
                aria-label={`Apagar ${member.login}`}
                onClick={() => {
                  if (window.confirm(`Apagar ${member.login} do lineup (engano)?`)) void onRemove();
                }}
              >
                Apagar
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {mode === 'edit' ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void onSave(fields).then((ok) => ok && setMode('view'));
          }}
        >
          <MemberFieldsForm value={fields} game={game} roles={roles} onChange={setFields} />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={busy}>
              Guardar
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setMode('view')}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : null}
      {mode === 'leave' ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void onLeave(leftOn).then((ok) => ok && setMode('view'));
          }}
        >
          <Field label="Saiu a">
            <DateInput value={leftOn} onChange={setLeftOn} min={member.joinedOn} />
          </Field>
          <Button type="submit" size="sm" disabled={busy}>
            Marcar saída
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setMode('view')}>
            Cancelar
          </Button>
        </form>
      ) : null}
    </div>
  );
}
